import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DataGrid, KpiCard, Spinner,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  Banknote, ChevronRight, IndianRupee, Plus, Printer, RefreshCw, Users,
} from 'lucide-react';
import { printPayslips, type PayslipBlock } from './payslipPrint';

/**
 * Payroll runs list — top-level page for the HR group.
 *
 * KPIs an HR/finance manager checks every cycle:
 *   - Total runs
 *   - Active employees
 *   - Net pay this month
 *   - Pending runs (drafts)
 *
 * One-click "Print payslips" downloads all payslips for any run as a
 * single A4 print-ready document.
 */

interface PayrollRunRow extends Record<string, unknown> {
  id?: number | string;
  payrollRunId?: string;
  period?: string;
  month?: number;
  year?: number;
  status?: string;
  totalEmployees?: number;
  totalGross?: number;
  totalNet?: number;
  totalDeductions?: number;
}

interface Payslip extends Record<string, unknown> {
  id?: number | string;
  payslipId?: string;
  payrollRunId?: string;
  employeeCode?: string;
  employeeName?: string;
  period?: string;
  daysWorked?: number;
  daysInMonth?: number;
  grossSalary?: number;
  totalDeductions?: number;
  netSalary?: number;
  earnings?: { name: string; amount: number }[];
  deductions?: { name: string; amount: number }[];
}

export default function PayrollRunList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PayrollRunRow[]>([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [r, e] = await Promise.all([
        apiClient.get('/hrm/PayrollRun').then(pickList<PayrollRunRow>).catch(() => []),
        apiClient.get('/hrm/Employee').then(pickList<{ isActive?: number | boolean }>).catch(() => []),
      ]);
      setRows(r);
      setEmployeeCount(e.filter((x) => !!x.isActive).length || e.length);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const kpis = useMemo(() => {
    const total = rows.length;
    const thisMonth = new Date().getMonth() + 1;
    const thisYear = new Date().getFullYear();
    const currentRun = rows.find(
      (r) => Number(r.month ?? 0) === thisMonth && Number(r.year ?? 0) === thisYear,
    );
    const pending = rows.filter((r) => {
      const s = String(r.status ?? '').toLowerCase();
      return s === 'draft' || s === 'approved' || s === 'pending';
    }).length;
    return {
      total,
      employeeCount,
      currentNet: Number(currentRun?.totalNet ?? 0),
      pending,
    };
  }, [rows, employeeCount]);

  async function printRun(runId: string | number) {
    setPrinting(String(runId));
    try {
      const r = await apiClient
        .get(`/hrm/Payslip?payrollRunId=${encodeURIComponent(String(runId))}`)
        .catch(() => apiClient.get('/hrm/Payslip'));
      const all = pickList<Payslip>(r);
      const slips = all
        .filter((p) => String(p.payrollRunId ?? '') === String(runId))
        .map<PayslipBlock>((p) => ({
          employeeCode: String(p.employeeCode ?? ''),
          employeeName: String(p.employeeName ?? ''),
          period: String(p.period ?? ''),
          daysWorked: Number(p.daysWorked ?? 0),
          daysInMonth: Number(p.daysInMonth ?? 0),
          earnings: Array.isArray(p.earnings) ? p.earnings : [],
          deductions: Array.isArray(p.deductions) ? p.deductions : [],
        }));
      if (slips.length === 0) {
        // No payslips returned (likely backend 500 / empty tenant) — print
        // a single empty placeholder so the user sees the print dialog.
        slips.push({
          employeeCode: '—',
          employeeName: 'No payslips found',
          period: rows.find((r) => String(r.payrollRunId ?? r.id) === String(runId))?.period ?? '',
          earnings: [],
          deductions: [],
        });
      }
      printPayslips(slips, 'KSS BillBook');
    } finally {
      setPrinting(null);
    }
  }

  const columns = useMemo<DataGridColumn<PayrollRunRow>[]>(
    () => [
      {
        accessor: 'period', title: 'Period', sortable: true, filterable: true,
        render: (r) => (
          <Link
            to={`/panel/payroll/runs/${encodeURIComponent(String(r.payrollRunId ?? r.id ?? ''))}`}
            className="font-medium text-primary hover:underline"
          >
            {String(r.period ?? `${r.month ?? ''}/${r.year ?? ''}`)}
          </Link>
        ),
      },
      { accessor: 'totalEmployees', title: 'Employees', sortable: true,
        render: (r) => Number(r.totalEmployees ?? 0).toLocaleString() },
      { accessor: 'totalGross', title: 'Gross', sortable: true,
        render: (r) => `₹ ${inr(Number(r.totalGross ?? 0))}` },
      { accessor: 'totalDeductions', title: 'Deductions', sortable: true,
        render: (r) => `₹ ${inr(Number(r.totalDeductions ?? 0))}` },
      { accessor: 'totalNet', title: 'Net pay', sortable: true,
        render: (r) => <span className="font-semibold">₹ {inr(Number(r.totalNet ?? 0))}</span> },
      { accessor: 'status', title: 'Status', sortable: true, filterable: true,
        render: (r) => <StatusPill v={String(r.status ?? '')} /> },
      {
        accessor: '__actions' as never, title: '', width: 180,
        render: (r) => {
          const id = String(r.payrollRunId ?? r.id ?? '');
          return (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => printRun(id)}
                disabled={printing === id}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
              >
                <Printer size={12} /> {printing === id ? 'Loading…' : 'Payslips'}
              </button>
              <Link
                to={`/panel/payroll/runs/${encodeURIComponent(id)}`}
                className="text-content-muted hover:text-primary"
              >
                <ChevronRight size={16} />
              </Link>
            </div>
          );
        },
      },
    ],
    [printing],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Payroll runs</h1>
          <p className="text-xs text-content-muted">
            Period-by-period payroll register · live data from /api/hrm/PayrollRun
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button size="sm" onClick={() => navigate('/panel/payroll/runs/new')}>
          <Plus className="h-3.5 w-3.5" /> Run payroll
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Payroll runs"
          value={kpis.total.toLocaleString()}
          subtitle="All periods"
          icon={<Banknote className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Active employees"
          value={kpis.employeeCount.toLocaleString()}
          subtitle="On rolls"
          icon={<Users className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Net pay this month"
          value={`₹ ${inr(kpis.currentNet)}`}
          subtitle={kpis.currentNet ? '✓ Run posted' : 'Not posted yet'}
          icon={<IndianRupee className={`h-5 w-5 ${kpis.currentNet ? 'text-success' : 'text-warning'}`} />}
        />
        <KpiCard
          title="Pending runs"
          value={kpis.pending.toLocaleString()}
          subtitle={kpis.pending ? 'Drafts to post' : 'Nothing pending'}
          icon={<RefreshCw className={`h-5 w-5 ${kpis.pending ? 'text-warning' : 'text-success'}`} />}
        />
      </div>

      {loading && rows.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading payroll runs…
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No payroll runs yet. Click <em>Run payroll</em> to issue this month&rsquo;s payslips.
          </div>
        </Card>
      ) : (
        <DataGrid<PayrollRunRow>
          data={rows}
          columns={columns}
          rowId={(rows[0]?.payrollRunId != null ? 'payrollRunId' : 'id') as keyof PayrollRunRow & string}
        />
      )}
    </div>
  );
}

function StatusPill({ v }: { v: string }) {
  const s = v.toLowerCase();
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (s === 'paid' || s === 'completed') cls = 'border-success/40 bg-success/10 text-success';
  else if (s === 'approved') cls = 'border-info/40 bg-info/10 text-info';
  else if (s === 'pending' || s === 'draft' || s === 'running') cls = 'border-warning/40 bg-warning/10 text-warning';
  else if (s === 'cancelled') cls = 'border-danger/40 bg-danger/10 text-danger';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] capitalize ${cls}`}>
      {v || '—'}
    </span>
  );
}

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
